/**
 * @file `useReportIssueMutation` — submit an in-lab learner issue report
 *       (#1672).
 * @module @labcraft/lab/ui/hooks/use-report-issue
 *
 * Thin wrapper over the `Lab` client's flat `reportIssue` method. The
 * caller (the app bar) assembles the full request — the learner's draft
 * plus the redacted-server-side support bundle — and hands it here; this
 * hook only carries the TanStack mutation lifecycle so the modal can
 * render pending / success / failure.
 *
 * Convention: hooks live in this directory and never call `useMutation`
 * outside it.
 */

import { useMutation } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { ReportRequest, ReportResponse } from '../../shared/routes.ts'

/**
 * Submit an issue report. The daemon redacts the message + command tail
 * and emails the report to the operator inbox (email-only, no persisted
 * record); resolves once the report is accepted for delivery.
 *
 * @returns The TanStack mutation over `ReportRequest → ReportResponse`.
 */
export function useReportIssueMutation() {
  const client = useClient<Lab>()
  return useMutation<ReportResponse, Error, ReportRequest>({
    mutationFn: (req: ReportRequest) => client.reportIssue(req),
  })
}
